import React, { useEffect, useState } from 'react'
import { CustomerModel } from 'shared/models/CutomerDeliveryLocations/CustomerModel'
import { DeliveryLocationModel } from 'shared/models/CutomerDeliveryLocations/DeliveryLocationModel'
import { DaysOfWeek, VestigingEnum } from 'shared/models/Enum'
import { IOrderType } from 'shared/models/IOrderType'
import { formatDate } from 'shared/service/helpers/DateUtilities'
import { IAfleverafspraakGroepKoppeling } from '../../../shared/models/IAfleverafspraakGroepKoppeling'
import { IAfleverafspraakLocatieKoppeling } from '../../../shared/models/IAfleverafspraakLocatieKoppeling'
import { IKlantAfleverafspraak } from '../../../shared/models/IKlantAfleverafspraak'

export type RowType =
  | DeliveryLocationModel
  | IKlantAfleverafspraak
  | IAfleverafspraakGroepKoppeling
  | IAfleverafspraakLocatieKoppeling

export function isAgreement(item: RowType): item is IKlantAfleverafspraak {
  return item != null && 'afleverafspraakReferentie' in item
}

export function isDeliveryLocation(
  item: RowType
): item is DeliveryLocationModel {
  return item != null && 'deliveryagreementlocationid' in item
}

export function isLocationLink(
  item: RowType
): item is IAfleverafspraakLocatieKoppeling {
  return item != null && 'afleverafspraakLocatieKoppelingId' in item
}

export function isGroupLink(
  item: RowType
): item is IAfleverafspraakGroepKoppeling {
  return item != null && 'afleverafspraakGroepKoppelingId' in item
}

interface IProps {
  customer: CustomerModel
  copyRow: IAfleverafspraakLocatieKoppeling
  possibleOrderTypes: IOrderType[]
  onRowSelected: (row: RowType) => void
}

export default (props: IProps) => {
  const [activeRow, setActiveRow] = useState<RowType>()
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerModel>()
  const [deliveryAgreementRows, setDeliveryAgreementRows] = useState<
    DeliveryLocationModel[]
  >([])
  const [selectedDeliveryAgreementRow, setSelectedDeliveryAgreementRow] =
    useState<DeliveryLocationModel>()

  const allDaysString: string = '1,2,3,4,5,6,0'
  const allOrderTypesString: string = '30,32,33,34,50'
  const alleForDisplay: string = 'Alle'

  useEffect(() => {
    if (props.customer != null) {
      setSelectedCustomer(props.customer)
    }
  }, [props.customer])

  useEffect(() => {
    if (
      selectedCustomer != null &&
      selectedCustomer.connectedAdresses != null &&
      selectedCustomer.connectedAdresses.length > 0
    ) {
      setDeliveryAgreementRows(selectedCustomer.connectedAdresses)
      selectRow(selectedCustomer.connectedAdresses[0])
    }
  }, [selectedCustomer])

  const selectRow = (row: RowType) => {
    setActiveRow(row)
    setSelectedDeliveryAgreementRow(row as DeliveryLocationModel)
    props.onRowSelected(row)
  }

  const getSortedRows = (data: DeliveryLocationModel[]) => {
    return data.sort((a, b) => {
      if (a.name > b.name) {
        return 1
      } else if (a.name < b.name) {
        return -1
      } else {
        if (a.startdate > b.startdate) {
          return 1
        } else if (a.startdate < b.startdate) {
          return -1
        } else {
          return 0
        }
      }
    })
  }

  const getDayNamesForDisplay = (days: string): string => {
    if (days === allDaysString) {
      return alleForDisplay
    }

    let result: string = ''
    const stringArray = days.split(',')
    stringArray.forEach(x => {
      const dayName = DaysOfWeek[Number(x)]
      if (result === '') {
        result += dayName
      } else {
        result += ',' + dayName
      }
    })
    return result
  }

  const renderRows = (agreementLocationLink: DeliveryLocationModel[]) => {
    var result = []

    getSortedRows(agreementLocationLink).map((x, index) => {
      result.push(renderLocationLinkRow(x, index))
    })

    return result
  }

  const renderLocationLinkRow = (
    agreementLocationLink: DeliveryLocationModel,
    index: number
  ) => {
    return (
      <tr
        key={index}
        onClick={() => selectRow(agreementLocationLink)}
        className={
          selectedDeliveryAgreementRow == agreementLocationLink
            ? 'tddata table-primary'
            : 'tddata'
        }
      >
        <td>
          {!!agreementLocationLink.warehouseid
            ? VestigingEnum[agreementLocationLink.warehouseid]
            : ''}
        </td>
        <td>
          {!!agreementLocationLink.deliveryagreementreference
            ? agreementLocationLink.deliveryagreementreference
            : ''}
        </td>
        <td>
          {!!agreementLocationLink.name ? agreementLocationLink.name : ''}
        </td>
        <td>
          {!!agreementLocationLink.deliveryagreementype
            ? agreementLocationLink.deliveryagreementype
            : ''}
        </td>
        <td>
          {!!agreementLocationLink.ordertype
            ? agreementLocationLink.ordertype == allOrderTypesString
              ? alleForDisplay
              : agreementLocationLink.ordertype
            : ''}
        </td>
        <td>
          {!!agreementLocationLink.days
            ? getDayNamesForDisplay(agreementLocationLink.days)
            : ''}
        </td>
        <td>
          {!!agreementLocationLink.shiptocustomergln
            ? agreementLocationLink.shiptocustomergln
            : ''}
        </td>
        <td>
          {!!agreementLocationLink.shiptolocationgln
            ? agreementLocationLink.shiptolocationgln
            : ''}
        </td>
        <td>
          {!!agreementLocationLink.shiptolocationname
            ? agreementLocationLink.shiptolocationname.substring(0, 24)
            : ''}
        </td>
        <td>
          {!!agreementLocationLink.shiptowarehousename
            ? agreementLocationLink.shiptowarehousename
            : ''}
        </td>
        <td>
          {!!agreementLocationLink.routetocustomergln
            ? agreementLocationLink.routetocustomergln
            : 'Zelfde'}
        </td>
        <td>
          {!!agreementLocationLink.routetolocationgln
            ? agreementLocationLink.routetolocationgln
            : ''}
        </td>
        <td>
          {!!agreementLocationLink.routetolocationname
            ? agreementLocationLink.routetolocationname
            : ''}
        </td>
        <td>
          {!!agreementLocationLink.routetowarehousename
            ? agreementLocationLink.routetowarehousename
            : ''}
        </td>
        <td>
          {!!agreementLocationLink.startdate
            ? formatDate(agreementLocationLink.startdate)
            : ''}
        </td>
        <td>
          {!!agreementLocationLink.enddate
            ? formatDate(agreementLocationLink.enddate)
            : ''}
        </td>
        <td>
          {!!agreementLocationLink.combination
            ? agreementLocationLink.combination
            : ''}
        </td>
      </tr>
    )
  }

  return (
    !!props.customer && (
      <table className='table table-hover'>
        <thead>
          <tr>
            <th
              colSpan={
                props.customer.connectedAdresses != null &&
                props.customer.connectedAdresses.length > 0
                  ? 11
                  : 13
              }
            >
              Gekoppelde adressen
            </th>
          </tr>
        </thead>
        <tbody>
          <tr className='tablesubheader'>
            <td>Vestiging</td>
            <td>AAR</td>
            <td>Naam</td>
            <td>Type</td>
            <td>OrderType</td>
            <td>Dagen</td>
            <td>
              Ship to <br /> Klant Gln
            </td>
            <td>
              Ship to <br /> Locatie Gln
            </td>
            <td>
              Ship to <br /> Locatie Naam
            </td>
            <td>
              Ship to <br /> Vestiging
            </td>
            <td>
              Route to <br /> Klant Gln
            </td>
            <td>
              Route to <br /> Locatie Gln
            </td>
            <td>
              Route to <br /> Locatie Naam
            </td>
            <td>
              Route to <br /> Vestiging
            </td>
            <td>Ingangsdatum</td>
            <td>Einddatum</td>
            <td>
              Code <br /> Combinatie
            </td>
          </tr>
          {deliveryAgreementRows != null &&
            deliveryAgreementRows.length > 0 &&
            renderRows(deliveryAgreementRows)}
        </tbody>
      </table>
    )
  )
}
